import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback } from 'react';
import useAwardSocialLinks from '../../../hooks/useAwardSocialLinks';
import UsageTracker from '../../../utils/UsageTracker';
import { useTrack } from '../../tracks';
import getLang from 'LearningCenterUI/utils/getLang';
import { useAchievementStatus } from '../achievementHooks';
import { SelectableButton } from 'foundations-components/transitional/components/SelectableButton';
import { SocialBlockFacebookIcon } from 'foundations-assets/icons/components/SocialBlockFacebookIcon';
import { SocialBlockLinkIcon } from 'foundations-assets/icons/components/SocialBlockLinkIcon';
import { SocialBlockLinkedinIcon } from 'foundations-assets/icons/components/SocialBlockLinkedinIcon';
import { SocialBlockXIcon } from 'foundations-assets/icons/components/SocialBlockXIcon';
import styled, { css } from 'foundations-theming/utils';
import { copyToClipboard } from '../../../utils/copyToClipboard';
import { mediaQuery, useMediaQuery } from 'academy-common-lib/utils/Styling';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const socialShareButtonDefinitions = [{
  networkName: 'facebook',
  hrefKey: 'facebook',
  icon: /*#__PURE__*/_jsx(SocialBlockFacebookIcon, {
    color: "do-not-use-facebook",
    size: "1000"
  }),
  buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.awardShareControls.facebook"
  })
}, {
  networkName: 'linkedin',
  hrefKey: 'linkedin',
  icon: /*#__PURE__*/_jsx(SocialBlockLinkedinIcon, {
    color: "do-not-use-linkedin",
    size: "1000"
  }),
  buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.awardShareControls.linkedinPost"
  })
}, {
  networkName: 'linkedin',
  hrefKey: 'linkedinAddToProfile',
  icon: /*#__PURE__*/_jsx(SocialBlockLinkedinIcon, {
    color: "do-not-use-linkedin",
    size: "1000"
  }),
  buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.awardShareControls.linkedinLicenses"
  })
}, {
  networkName: 'x',
  hrefKey: 'x',
  icon: /*#__PURE__*/_jsx(SocialBlockXIcon, {
    color: "do-not-use-x",
    size: "1000"
  }),
  buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.awardShareControls.xSocialNetwork"
  })
}];
const ButtonGrid = styled.div.withConfig({
  displayName: "SocialShareButtons__ButtonGrid"
})(["display:grid;gap:", ";grid-template-columns:repeat(1,1fr);", " ", ""], ({
  theme
}) => theme.space['300'], mediaQuery('notMobile', css(["grid-template-columns:repeat(2,1fr);"])), mediaQuery('notLaptop', css(["grid-template-columns:repeat(5,1fr);"])));
const SocialShareButtons = ({
  award,
  trackId,
  language
}) => {
  const {
    isLaptop
  } = useMediaQuery();
  const shareLinks = useAwardSocialLinks(trackId.toString(), language);
  const {
    track
  } = useTrack(Number(trackId), getLang());
  const {
    isDisabled
  } = useAchievementStatus(trackId.toString());
  const onSocialShareClick = useCallback((socialName, destinationUrl) => {
    if (destinationUrl) {
      window.open(destinationUrl, '_blank');
    }
    if (!award) return;
    UsageTracker.track('awardInteraction', {
      action: 'social-share',
      trackId: trackId.toString(),
      trackName: track ? track.name : null,
      view: 'sharing-controls',
      socialName,
      destinationUrl: destinationUrl || ''
    });
  }, [award, track, trackId]);
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsxs(ButtonGrid, {
      children: [shareLinks && socialShareButtonDefinitions.map(({
        networkName,
        hrefKey,
        icon,
        buttonText
      }) => /*#__PURE__*/_jsx(Tooltip, {
        title: isDisabled ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.awardShareControls.disabledTooltip"
        }) : undefined,
        children: /*#__PURE__*/_jsx(SelectableButton, {
          "data-test-id": "social-sharing-option",
          "data-test-url": shareLinks[hrefKey],
          disabled: isDisabled,
          maxInlineSize: isLaptop ? '100%' : undefined,
          onClick: () => onSocialShareClick(networkName, shareLinks[hrefKey]),
          selected: false,
          size: isLaptop ? 'sm' : 'lg',
          title: buttonText,
          children: icon
        })
      }, `${networkName}-${hrefKey}`)), /*#__PURE__*/_jsx(SelectableButton, {
        disabled: isDisabled,
        maxInlineSize: isLaptop ? '100%' : undefined,
        onClick: async () => {
          try {
            if (shareLinks !== null && shareLinks !== void 0 && shareLinks.achievementUrl) {
              await copyToClipboard(shareLinks === null || shareLinks === void 0 ? void 0 : shareLinks.achievementUrl);
            }
            onSocialShareClick('copy-link', shareLinks === null || shareLinks === void 0 ? void 0 : shareLinks.achievementUrl);
          } catch (error) {
            console.error('Failed to copy to clipboard:', error);
          }
        },
        selected: false,
        size: isLaptop ? 'sm' : 'lg',
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.awardShareControls.copyLink"
        }),
        children: /*#__PURE__*/_jsx(SocialBlockLinkIcon, {
          color: "icon-core-subtle",
          size: "1000"
        })
      })]
    })
  );
};
export default SocialShareButtons;